import {
  computeEexQuoteBook,
  eexQuoteMonthLabel,
  eexQuoteRowHasMarketInputs,
} from "@chro/shared";
import { Copy, Layers3, Play, Plus, Trash2 } from "lucide-react";
import { useMemo, useState } from "react";
import type { QuoteComponent, QuoteSnapshot } from "./model";

interface QuoteComponentsPanelProps {
  snapshot: QuoteSnapshot;
  selectedMonths: string[];
  canWrite: boolean;
  onAdd(component: QuoteComponent): void;
  onUpdate(component: QuoteComponent): void;
  onDelete(component: QuoteComponent): void;
  onApply(component: QuoteComponent): void;
  onCopy(component: QuoteComponent): void;
}

export function QuoteComponentsPanel({
  snapshot,
  selectedMonths,
  canWrite,
  onAdd,
  onUpdate,
  onDelete,
  onApply,
  onCopy,
}: QuoteComponentsPanelProps) {
  const [name, setName] = useState("");
  const [mw, setMw] = useState("1");
  const suggestedName = selectionLabel(selectedMonths);
  const parsedMw = Number(mw);
  const canAdd =
    canWrite &&
    selectedMonths.length > 0 &&
    mw.trim().length > 0 &&
    Number.isFinite(parsedMw) &&
    Math.abs(parsedMw) <= 1_000_000_000 &&
    snapshot.components.length < 100;

  return (
    <div className="min-h-0 flex-1 overflow-y-auto p-3 text-xs">
      <div className="rounded border border-accent/30 bg-accent/5 p-3 text-[11px] text-muted">
        Select any grid cells, rows, or disjoint ranges with Shift and Ctrl/Cmd, then save those delivery months as an independent quote strip.
      </div>

      <fieldset disabled={!canWrite} className="mt-3 rounded-md border border-border/70 p-3 disabled:opacity-60">
        <legend className="px-1 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">New strip from selection</legend>
        <div className="mb-2 rounded bg-border/25 px-2 py-1.5 text-[10px] text-muted">
          {selectedMonths.length > 0
            ? `${selectedMonths.length} month${selectedMonths.length === 1 ? "" : "s"} selected · ${suggestedName}`
            : "No delivery months selected in the grid"}
        </div>
        <label className="block space-y-1 text-[10px] text-muted">
          <span>Name</span>
          <input
            value={name}
            maxLength={120}
            onChange={(event) => setName(event.target.value)}
            placeholder={suggestedName || "e.g. Cal-29 or Q4 + Cal"}
            className="h-8 w-full rounded border border-border bg-bg px-2 text-xs text-fg outline-none focus:border-accent"
          />
        </label>
        <label className="mt-2 block space-y-1 text-[10px] text-muted">
          <span>Flat volume MW</span>
          <input
            type="number"
            value={mw}
            min={-1_000_000_000}
            max={1_000_000_000}
            step="0.01"
            onChange={(event) => setMw(event.target.value)}
            className="h-8 w-full rounded border border-border bg-bg px-2 text-right font-mono text-xs text-fg outline-none focus:border-accent"
          />
        </label>
        <button
          type="button"
          disabled={!canAdd}
          onClick={() => {
            onAdd({
              id: globalThis.crypto.randomUUID(),
              name: name.trim() || suggestedName,
              months: [...new Set(selectedMonths)].sort(),
              mw: parsedMw,
            });
            setName("");
          }}
          className="mt-3 inline-flex h-8 w-full items-center justify-center gap-1.5 rounded bg-accent text-[11px] font-medium text-white hover:brightness-105 disabled:opacity-40"
        >
          <Plus className="h-3.5 w-3.5" /> Save selected months as strip
        </button>
      </fieldset>

      <div className="mb-2 mt-5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-[0.1em] text-muted">
        <Layers3 className="h-3.5 w-3.5" /> Named quote strips · {snapshot.components.length}
      </div>
      {snapshot.components.length === 0 && (
        <div className="rounded border border-dashed border-border px-3 py-6 text-center text-[11px] text-muted">
          No named strips yet. The strip list is saved inside quotes and presets.
        </div>
      )}
      <div className="space-y-2.5">
        {snapshot.components.map((component) => (
          <ComponentCard
            key={component.id}
            component={component}
            snapshot={snapshot}
            canWrite={canWrite}
            onUpdate={onUpdate}
            onDelete={onDelete}
            onApply={onApply}
            onCopy={onCopy}
          />
        ))}
      </div>
    </div>
  );
}

function ComponentCard({
  component,
  snapshot,
  canWrite,
  onUpdate,
  onDelete,
  onApply,
  onCopy,
}: {
  component: QuoteComponent;
  snapshot: QuoteSnapshot;
  canWrite: boolean;
  onUpdate(component: QuoteComponent): void;
  onDelete(component: QuoteComponent): void;
  onApply(component: QuoteComponent): void;
  onCopy(component: QuoteComponent): void;
}) {
  const result = useMemo(() => componentBook(snapshot, component), [component, snapshot]);
  return (
    <div data-quote-component-id={component.id} className="rounded-md border border-border/70 p-2.5">
      <div className="flex items-start gap-2">
        <div className="min-w-0 flex-1">
          <input
            value={component.name}
            aria-label={`${component.name} strip name`}
            disabled={!canWrite}
            maxLength={120}
            onChange={(event) => {
              if (event.target.value.trim()) onUpdate({ ...component, name: event.target.value });
            }}
            className="h-6 w-full truncate border-b border-transparent bg-transparent text-xs font-semibold outline-none focus:border-accent disabled:text-fg"
          />
          <div className="text-[10px] text-muted">
            {selectionLabel(component.months)} · {component.months.length} month{component.months.length === 1 ? "" : "s"}
          </div>
        </div>
        <label className="flex h-7 w-20 items-center rounded border border-border text-[9px] text-muted focus-within:border-accent">
          <input
            type="number"
            value={component.mw}
            aria-label={`${component.name} MW`}
            disabled={!canWrite}
            min={-1_000_000_000}
            max={1_000_000_000}
            step="0.01"
            onChange={(event) => {
              const value = Number(event.target.value);
              if (Number.isFinite(value) && Math.abs(value) <= 1_000_000_000) {
                onUpdate({ ...component, mw: value });
              }
            }}
            className="min-w-0 flex-1 bg-transparent px-1 text-right font-mono text-[11px] text-fg outline-none"
          />
          <span className="pr-1">MW</span>
        </label>
      </div>

      <div className="mt-2 grid grid-cols-2 gap-px overflow-hidden rounded border border-border/60 bg-border/60">
        <Metric label="Energy" value={format(result.book.totals.totalMwh, 0)} unit="MWh" />
        <Metric label="Curve coverage" value={`${result.available}/${component.months.length}`} unit="months" />
        <Metric label="Physical RON" value={`${format(result.book.totals.buyRon, 2)} / ${format(result.book.totals.sellRon, 2)}`} unit="buy / sell" accent />
        <Metric label="Physical EUR" value={`${format(result.book.totals.buyEur, 2)} / ${format(result.book.totals.sellEur, 2)}`} unit="buy / sell" />
      </div>
      {result.available < component.months.length && (
        <div className="mt-1 text-[9px] text-warn-orange">
          {component.months.length - result.available} month(s) fall outside the current curve horizon.
        </div>
      )}
      {result.available === component.months.length && !result.marketInputsReady && (
        <div className="mt-1 text-[9px] text-warn-orange">
          Complete HU, quoted basis, risk, and positive FX for every strip month before copying.
        </div>
      )}

      <div className="mt-2 flex items-center gap-1">
        <button type="button" aria-label={`Load ${component.name} profile`} disabled={!canWrite} onClick={() => onApply(component)} className="inline-flex h-7 items-center gap-1 rounded border border-accent/50 px-2 text-[10px] text-accent hover:bg-accent/10 disabled:opacity-40">
          <Play className="h-3 w-3" /> Load profile
        </button>
        <button
          type="button"
          aria-label={`Copy ${component.name} strip`}
          disabled={!snapshot.metadata.inputsConfirmed || !result.marketInputsReady || component.mw === 0}
          onClick={() => onCopy(component)}
          title={
            result.available !== component.months.length
              ? "Load a curve covering every strip month before copying"
              : !result.marketInputsReady
                ? "Complete the market inputs for every strip month before copying"
                : component.mw === 0
                  ? "Enter a non-zero strip volume before copying"
              : snapshot.metadata.inputsConfirmed
                ? "Copy this strip quote"
                : "Confirm market inputs before copying"
          }
          className="inline-flex h-7 items-center gap-1 rounded border border-border px-2 text-[10px] text-muted hover:border-accent/50 hover:text-fg disabled:opacity-40"
        >
          <Copy className="h-3 w-3" /> Copy
        </button>
        <button type="button" aria-label={`Delete ${component.name} strip`} disabled={!canWrite} onClick={() => { if (window.confirm(`Delete strip “${component.name}”?`)) onDelete(component); }} className="ml-auto rounded p-1.5 text-muted hover:bg-neg-red/10 hover:text-neg-red disabled:opacity-40" title="Delete strip">
          <Trash2 className="h-3.5 w-3.5" />
        </button>
      </div>
    </div>
  );
}

function componentBook(snapshot: QuoteSnapshot, component: QuoteComponent) {
  const wanted = new Set(component.months);
  const sourceRows = snapshot.rows.filter((row) => wanted.has(row.month));
  const rows = sourceRows.map((row) => ({ ...row, mw: component.mw }));
  return {
    available: rows.length,
    marketInputsReady:
      rows.length === component.months.length && sourceRows.every(eexQuoteRowHasMarketInputs),
    book: computeEexQuoteBook(rows, snapshot.settings),
  };
}

function selectionLabel(months: readonly string[]): string {
  if (months.length === 0) return "";
  const sorted = [...months].sort();
  const first = eexQuoteMonthLabel(sorted[0]!);
  const last = eexQuoteMonthLabel(sorted[sorted.length - 1]!);
  return first === last ? first : `${first} – ${last}`;
}

function Metric({ label, value, unit, accent = false }: { label: string; value: string; unit: string; accent?: boolean }) {
  return (
    <div className={accent ? "bg-[#d0beff]/30 p-2 dark:bg-[#40325f]/40" : "bg-bg p-2"}>
      <div className="text-[8px] font-semibold uppercase tracking-[0.08em] text-muted">{label}</div>
      <div className="truncate font-mono text-[11px] font-semibold tabular-nums" title={value}>{value}</div>
      <div className="text-[8px] text-muted">{unit}</div>
    </div>
  );
}

function format(value: number | null, decimals: number): string {
  if (value == null || !Number.isFinite(value)) return "—";
  return new Intl.NumberFormat("en-GB", {
    minimumFractionDigits: decimals,
    maximumFractionDigits: decimals,
  }).format(value);
}
